import {PERIOD_BUDGET_TYPE, PERIODICITY} from '../../../constants';
import type {BaselineBuilderEntryPoint} from '../../../tracking/amplitude/baselineBuilderEvents';

export type Phase = 'idle' | 'running' | 'awaiting_approval' | 'committing' | 'committed' | 'error';

// The Retainer budget type's own per-period settings (TNSR-635 review — Figma's "Create New
// Opportunity/Retainer" frames, nodes 336:10082/11755/12493/13770). Lifted to the modal (like
// duplicateScope) because, unlike Rate Card/Use Baseline (D8: real but never submitted), these
// fields ARE submitted — CreateProjectInput's own defaultPeriod* fields — so handleApprove needs
// them at submit time, not just FinancialSetupSection's own local state.
export type RetainerScope = {
	periodicity: string;
	periodLength: number;
	periodBudgetType: string;
	periodHoursAmount: number | null;
	periodPriceAmount: number | null;
	preCreateAmount: number;
};

export const DEFAULT_RETAINER_SCOPE: RetainerScope = {
	periodicity: PERIODICITY.MONTHLY,
	periodLength: 1,
	periodBudgetType: PERIOD_BUDGET_TYPE.FIXED_HOURS,
	periodHoursAmount: null,
	periodPriceAmount: null,
	preCreateAmount: 1,
};

// A candidate source project for the Duplicate Project flow (TNSR-635 review — Figma's "Copy
// Existing Project"/"Project to Copy" fields, in the Details section below Labels).
export type DuplicateableProject = {
	id: string;
	name: string;
	companyProjectId: number;
	customProjectId: string | null;
	useDeliverables: boolean;
};

// The granular clone-scope checkboxes Figma shows once a source project is picked (Structure /
// Resourcing / Financials / Setup groups) — mirrors CreateOpportunityModal.tsx's own identical
// concept (duplicateSettings/duplicateTasks/etc.) field-for-field, since that's the one other place
// in the codebase DuplicateProjectMutation is already driven from a design-system UI.
export type DuplicateScopeOptions = {
	settings: boolean;
	tasks: boolean;
	phases: boolean;
	team: boolean;
	allocations: boolean;
	expenses: boolean;
	deliverables: boolean;
	retainerPeriods: boolean;
	placeholders: boolean;
	color: boolean;
};

// Duplicating starts from "copy everything" — matches CreateOpportunityModal.tsx's own defaults
// (every duplicate* useState there inits to true).
export const DEFAULT_DUPLICATE_SCOPE: DuplicateScopeOptions = {
	settings: true,
	tasks: true,
	phases: true,
	team: true,
	allocations: true,
	expenses: true,
	deliverables: true,
	retainerPeriods: true,
	placeholders: true,
	color: true,
};

export interface ExtractedDeliverable {
	name: string;
	description?: string | null;
	start_date?: string | null;
	end_date?: string | null;
	total_hours?: number | null;
}

export interface ExtractedInvoice {
	name: string;
	unit_price: number;
	due_date?: string | null;
	invoice_type: string;
}

export interface TeamMember {
	person_id: number;
	name: string;
	role: string;
	available_hours: number;
	is_overallocated: boolean;
	allocation_minutes_per_day: number | null;
}

export interface ProjectProposal {
	name: string;
	start_date: string | null;
	end_date: string | null;
	budget_type: string;
	budget: number | null;
	color: string;
	team_members: TeamMember[];
	unfilled_roles?: string[];
	client_id: number | null;
	client_name: string | null;
	owner_name?: string | null;
	owner_person_id?: number | null;
	deliverables: ExtractedDeliverable[];
	// Optional, unlike deliverables: a proposal predating this feature (a restored session, a
	// reconciliation payload from before invoicing was wired in) genuinely omits these — every
	// consumer already reads them as `proposal.invoices?.length ?? 0` defensively, so the type
	// should say what the code actually defends against.
	invoices?: ExtractedInvoice[];
	unsupported_billing_terms?: string[];
}

export interface Warning {
	type: string;
	message: string;
	person_id: number | null;
	role: string | null;
}

export interface ChatMessage {
	id: string;
	role: 'user' | 'assistant' | 'tool';
	content: string;
	toolName?: string;
	callCount?: number;
	isStreaming?: boolean;
	questionOptions?: string[];
	questionId?: string;
}

export type PlaceholderStatus = 'pending' | 'creating' | 'done' | 'failed';

export interface PlaceholderProgress {
	name: string;
	status: PlaceholderStatus;
}

export type SowStepStatus = 'pending' | 'active' | 'done';

export interface SowExtractionStep {
	label: string;
	status: SowStepStatus;
}

export interface ReviewRequestedPayload {
	proposal: ProjectProposal;
	nova_filled_fields: string[];
	required_fields_status: Record<string, boolean>;
	sow_filename: string | null;
	create_placeholders_enabled: boolean;
	invoices_enabled: boolean;
	placeholder_role_names?: string[];
	// full | deliverables_only | roles_only | metadata_only | empty. Absent from an older agent.
	extraction_outcome?: string;
}

export interface ReconciliationQuestion {
	id: string;
	question_type: string;
	question: string;
	options: string[];
	field_ref: string;
	best_guess: string | null;
	// Same values as ReviewRequestedPayload.extraction_outcome. Absent from an older agent.
	extraction_outcome?: string;
}

// What a committed stream reports beyond the created counts. Each field is absent when the agent
// did not send it; skippedRoleReasons carries only the reason codes, never the role names.
export interface CommitDetails {
	skippedDeliverableCount?: number;
	skippedRoleReasons?: string[];
}

export interface StreamCallbacks {
	onText: (messageId: string, delta: string) => void;
	onMessageStreamEnd: (messageId: string) => void;
	onProposal: (proposal: ProjectProposal) => void;
	onWarnings: (warnings: Warning[]) => void;
	onCommitted: (
		projectId: number | null,
		relayProjectId?: string,
		deliverableCount?: number,
		roleCount?: number,
		baselineNotices?: string[],
		details?: CommitDetails
	) => void;
	// The commit failed and was rolled back. failureCode is absent from an older agent.
	onCommitFailed?: (failureCode?: string) => void;
	onRefiningStarted: () => void;
	onFinished: () => void;
	onError: (message: string) => void;
	// The account has spent its AI allowance for the period. Separate from
	// onError because it is not a fault: the caller shows the message as ordinary
	// output and leaves the user a way to carry on manually, rather than styling
	// it as a failure. Optional so an existing caller keeps compiling.
	onBudgetExhausted?: (message: string) => void;
	onToolCall: (toolCallId: string, toolName: string) => void;
	onToolResult: (toolCallId: string) => void;
	onNewAssistantMessage: (messageId: string) => void;
	onStepStarted?: (stepName: string) => void;
	onStepFinished?: (stepName: string) => void;
	onReviewRequested?: (payload: ReviewRequestedPayload) => void;
	onReconciliationQuestion?: (question: ReconciliationQuestion) => void;
	onPlaceholderProgress?: (placeholders: PlaceholderProgress[]) => void;
	onClassificationBlocked?: (reason?: string, blockCode?: string) => void;
}

export interface AttachedFile {
	filename: string;
	mimeType: string;
	dataBase64: string;
}

export interface AgentEvent {
	type?: string;
	messageId?: string;
	delta?: string;
	toolCallId?: string;
	toolCallName?: string;
	step?: string;
	stepName?: string;
	message?: string;
	proposal?: ProjectProposal;
	warnings?: Warning[];
	project_id?: number;
	relay_project_id?: string;
	deliverable_count?: number;
	role_count?: number;
	// Plain-text sentences naming roles the agent could not add to the Baseline, and why.
	baseline_notices?: string[];
	nova_filled_fields?: string[];
	required_fields_status?: Record<string, boolean>;
	sow_filename?: string | null;
	create_placeholders_enabled?: boolean;
	invoices_enabled?: boolean;
	id?: string;
	question_type?: string;
	question?: string;
	options?: string[];
	field_ref?: string;
	best_guess?: string | null;
	block_reason?: string;
	block_code?: string;
	placeholders?: PlaceholderProgress[];
	skipped_deliverables?: unknown[];
	skipped_roles?: {name?: string; reason?: string}[];
	failure_code?: string;
}

export interface AiProjectPlanningModalProps {
	closeModal: () => void;
	personId?: number;
	initialFile?: AttachedFile;
	// Called when the user chooses to leave a rejected SoW and set up the project
	// manually instead. The caller is responsible for closing this modal and opening
	// the general project creator (mirrors ProjectCreationChoiceModal's onManual).
	onSwitchToManualCreate?: () => void;
	// Gates the "Have a Statement of Work?" promo/drop-zone at the top of the form —
	// nothing else. This is now the sole project-creation modal (ProjectCreationChoiceModal
	// is retired), so a viewer without the forecast_agent_planning flag/MANAGE_ACCOUNT_SETTINGS
	// permission still gets this modal, just without the SoW option: the promo stays hidden and
	// they fill in the form manually, matching product's ruling on TNSR-635.
	aiEnabled: boolean;
	// TNSR-532's global gen-ai kill switch (isGenAiDisabled(), landed on master after this modal
	// was built). Distinct from aiEnabled: aiEnabled=false means the account never had the SoW
	// capability, so the promo is fully absent; aiDisabled=true means it exists but has been
	// switched off, so the promo stays visible, greyed, and explains why — matching the retired
	// ProjectCreationChoiceModal's own established treatment for this flag.
	aiDisabled?: boolean;
	// True when project_create_modal_redesign is OFF (computed by the caller) — restores the
	// pre-TNSR-635 SoW review experience: a full-screen extraction step with no form underneath,
	// then a two-panel review (this same form, left, plus the Nova ChatPanel, right) once extraction
	// finishes, instead of D11's permanently-mounted single-column form. Only changes how the SoW
	// path (uploadRegionState !== 'no-file') is laid out — manual (no-file) creation through this
	// modal, reachable only when aiEnabled is already true, is unaffected.
	legacySowReview?: boolean;
	// Where the viewer started creating the project from, reported when this modal opens the
	// Baseline Builder session itself.
	entryPoint?: BaselineBuilderEntryPoint;
}
